import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Shot } from "../api/types";
import {
  ensureAudioContext,
  estimateDurationSeconds,
  isMediaRecordingSupported,
  RenderCancelled,
  renderSlideshow,
  type RenderHandle,
  type RenderProgress,
} from "../video/renderSlideshow";

/** "10 seconds", "70 seconds", "1.5 minutes" — for human-facing copy. */
function humanizeSeconds(s: number): string {
  if (s < 90) return `${s} seconds`;
  const minutes = Math.round((s / 60) * 2) / 2; // nearest half-minute
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

interface Props {
  /** Shots to animate, in whatever order — the renderer sorts chronologically. */
  shots: Shot[];
  /** Label for the source view, used in the download filename. */
  sourceLabel: string;
  onClose: () => void;
}

type Phase =
  | { kind: "confirm" }
  | { kind: "working"; progress: RenderProgress }
  | { kind: "ready"; url: string; mimeType: string }
  | { kind: "error"; message: string };

function downloadName(sourceLabel: string): string {
  const slug = sourceLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const stamp = new Date().toISOString().slice(0, 10);
  return `buacam-${slug || "animation"}-${stamp}.webm`;
}

/**
 * Self-contained "View as animation" control: a chip-styled button that opens
 * the {@link AnimationDialog}. Drop it into a toolbar with the shots that are
 * currently in view. Disabled (with a hint) when there's nothing to animate.
 */
export function AnimateButton({
  shots,
  sourceLabel,
}: {
  shots: Shot[];
  sourceLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const empty = shots.length === 0;
  return (
    <>
      <button
        type="button"
        className="anim-trigger"
        onClick={() => setOpen(true)}
        disabled={empty}
        title={
          empty
            ? "No frames to animate"
            : "View these frames as a 30-second film"
        }
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
        </svg>
        View as animation
      </button>
      {open && (
        <AnimationDialog
          shots={shots}
          sourceLabel={sourceLabel}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function AnimationDialog({ shots, sourceLabel, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  const handleRef = useRef<RenderHandle | null>(null);
  // Latest object URL, tracked separately so cleanup runs even if state has
  // already moved on (e.g. closed mid-render).
  const urlRef = useRef<string | null>(null);

  const supported = isMediaRecordingSupported();
  // ~10s per frame, so the runtime grows with the selection.
  const estDuration = humanizeSeconds(estimateDurationSeconds(shots.length));

  const teardown = useCallback(() => {
    handleRef.current?.cancel();
    handleRef.current = null;
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // Cancel any in-flight render and revoke the blob URL when the dialog leaves.
  useEffect(() => teardown, [teardown]);

  // Esc closes, mirroring the Lightbox. Also lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const startRender = useCallback(() => {
    setPhase({ kind: "working", progress: { phase: "loading", progress: 0 } });
    // Prime the shared AudioContext *here*, synchronously inside the click
    // handler, so the browser's autoplay policy lets it run. Doing it later
    // (after the async frame load) would fall outside the user gesture and
    // resume() could hang — the "stuck at Gathering frames" bug.
    const audioContext = ensureAudioContext() ?? undefined;
    const handle = renderSlideshow(shots, {
      audioContext,
      onProgress: (progress) => setPhase({ kind: "working", progress }),
    });
    handleRef.current = handle;
    handle.promise
      .then(({ blob, mimeType }) => {
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setPhase({ kind: "ready", url, mimeType });
      })
      .catch((err) => {
        if (err instanceof RenderCancelled) return; // user backed out
        setPhase({
          kind: "error",
          message: err instanceof Error ? err.message : "Something went wrong.",
        });
      });
  }, [shots]);

  const cancelRender = useCallback(() => {
    handleRef.current?.cancel();
    handleRef.current = null;
    setPhase({ kind: "confirm" });
  }, []);

  const pct =
    phase.kind === "working" ? Math.round(phase.progress.progress * 100) : 0;
  const statusLabel =
    phase.kind === "working"
      ? phase.progress.phase === "loading"
        ? "Gathering frames…"
        : `Rendering your film… ${pct}%`
      : "";

  return createPortal(
    <div
      className="anim-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Create animation"
      onClick={onClose}
    >
      <div className="anim-dialog" onClick={(e) => e.stopPropagation()}>
        {phase.kind === "confirm" && (
          <>
            <div className="anim-eyebrow">Forest film</div>
            <h2 className="anim-title serif">View as animation</h2>
            {supported ? (
              <p className="anim-body">
                A video file will be prepared from {shots.length}{" "}
                {shots.length === 1 ? "frame" : "frames"} and will be available
                for download shortly. It plays as a slideshow — about{" "}
                {estDuration} — with gentle transitions and a laid-back lounge
                soundtrack.
              </p>
            ) : (
              <p className="anim-body">
                Sorry — your browser doesn't support in-page video recording, so
                the animation can't be created here. Try a recent version of
                Chrome, Edge, or Firefox.
              </p>
            )}
            <div className="anim-actions">
              <button className="anim-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="anim-btn primary"
                onClick={startRender}
                disabled={!supported || shots.length === 0}
              >
                OK
              </button>
            </div>
          </>
        )}

        {phase.kind === "working" && (
          <>
            <div className="anim-eyebrow">Preparing</div>
            <h2 className="anim-title serif">Rolling film…</h2>
            <p className="anim-body">
              Stitching your frames together. Rendering runs in real time, so
              this takes about {estDuration} — please keep this tab in the
              foreground.
            </p>
            <div
              className="anim-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div className="anim-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="anim-status">{statusLabel}</div>
            <div className="anim-actions">
              <button className="anim-btn" onClick={cancelRender}>
                Cancel
              </button>
            </div>
          </>
        )}

        {phase.kind === "ready" && (
          <>
            <div className="anim-eyebrow">Ready</div>
            <h2 className="anim-title serif">Your forest film</h2>
            <video className="anim-video" src={phase.url} controls autoPlay loop />
            <div className="anim-actions">
              <button className="anim-btn" onClick={onClose}>
                Close
              </button>
              <a
                className="anim-btn primary"
                href={phase.url}
                download={downloadName(sourceLabel)}
              >
                Download video
              </a>
            </div>
          </>
        )}

        {phase.kind === "error" && (
          <>
            <div className="anim-eyebrow">Couldn't finish</div>
            <h2 className="anim-title serif">Render failed</h2>
            <p className="anim-body">{phase.message}</p>
            <div className="anim-actions">
              <button className="anim-btn" onClick={onClose}>
                Close
              </button>
              <button className="anim-btn primary" onClick={startRender}>
                Try again
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
